<div class="project-settings-container">
  <!-- Tabbed Settings Layout (Issue #239) - Full screen -->
  <mat-tab-group
    class="settings-tabs"
    [mat-stretch-tabs]="false"
    [selectedIndex]="selectedTabIndex"
    (selectedTabChange)="onTabChange($event)"
  >
    <!-- Overview Tab -->
    <mat-tab label="Overview">
      <div class="tab-content">
        <!-- Legacy Project Banner (Issue #227) -->
        <app-legacy-project-banner
          [show]="!loadingMetadata && !hasProjectMetadata"
          (setupClick)="openMetadataSetupDialog()"
          (dismiss)="onBannerDismiss()"
        ></app-legacy-project-banner>
        <!-- Two-Column Grid: Project Metadata + Location Map (Issue #236) -->
        <div class="metadata-map-container">
          <!-- Left Column: Project Metadata -->
          <mat-card class="settings-section metadata-card">
            <mat-card-header>
              <div class="header-with-actions">
                <div class="header-content">
                  <mat-icon class="header-icon">info</mat-icon>
                  <div class="header-text">
                    <mat-card-title>Project Information</mat-card-title>
                    @if (projectMetadata) {
                      <mat-card-subtitle>ID: {{ projectMetadata.project_id }}</mat-card-subtitle>
                    }
                  </div>
                </div>
                @if (hasProjectMetadata && !loadingMetadata) {
                  <div class="header-actions">
                    @if (hasUnsavedChanges) {
                      <div class="action-buttons">
                        <button
                          mat-mini-fab
                          class="discard-button"
                          aria-label="Discard project metadata changes"
                          (click)="discardChanges()"
                          [disabled]="savingMetadata"
                          matTooltip="Discard changes"
                          matTooltipPosition="below"
                        >
                          <mat-icon>undo</mat-icon>
                        </button>
                        <button
                          mat-mini-fab
                          class="save-button"
                          aria-label="Save project metadata changes"
                          (click)="saveMetadata()"
                          [disabled]="savingMetadata"
                          [matTooltip]="savingMetadata ? 'Saving...' : 'Save changes'"
                          matTooltipPosition="below"
                        >
                          <mat-icon>{{ savingMetadata ? 'hourglass_empty' : 'check' }}</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }
              </div>
            </mat-card-header>
            <mat-card-content>
              @if (loadingMetadata) {
                <mat-progress-bar mode="indeterminate"></mat-progress-bar>
              }

              @if (!loadingMetadata && projectMetadata) {
                <div class="metadata-grid">
                  <!-- Project Name (Issue #227 - Story 1: Inline Editable) -->
                  <div class="metadata-item full-width-item compact-item">
                    <mat-form-field appearance="fill" class="full-width-field">
                      <mat-label>Project Name</mat-label>
                      <input
                        matInput
                        data-testid="project-name-input"
                        [(ngModel)]="editedProjectName"
                        (ngModelChange)="onFieldChange()"
                        placeholder="Enter project name"
                      />
                      @if (nameChanged) {
                        <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
                      }
                    </mat-form-field>
                  </div>
                  <!-- Project Description (Issue #227 - Story 2: Inline Editable) -->
                  <div class="metadata-item full-width-item compact-item">
                    <mat-form-field appearance="fill" class="full-width-field">
                      <mat-label>Description</mat-label>
                      <textarea
                        matInput
                        data-testid="project-description-input"
                        [(ngModel)]="editedDescription"
                        (ngModelChange)="onFieldChange()"
                        rows="3"
                        placeholder="Enter description"
                      ></textarea>
                      @if (descriptionChanged) {
                        <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
                      }
                    </mat-form-field>
                  </div>
                  <!-- Project Address (Issue #227 - Phase 1: Inline Editable) -->
                  <div class="metadata-item full-width-item compact-item">
                    <span class="field-label">Address</span>
                    <div class="address-fields">
                      <mat-form-field appearance="fill" class="full-width-field">
                        <mat-label>Street</mat-label>
                        <input
                          matInput
                          #streetAddressInput
                          data-testid="project-address-street"
                          [(ngModel)]="editedAddress.street"
                          (ngModelChange)="onStreetInputChange($event)"
                          [matAutocomplete]="addressAuto"
                          placeholder="Start typing to see suggestions..."
                          autocomplete="nope"
                          name="street-address"
                        />
                        <mat-autocomplete
                          #addressAuto="matAutocomplete"
                          (optionSelected)="onAddressSuggestionSelected($event)"
                          [displayWith]="displaySuggestion"
                        >
                          @for (suggestion of addressSuggestions; track suggestion.placeId) {
                            <mat-option [value]="suggestion">
                              {{ suggestion.text }}
                            </mat-option>
                          }
                        </mat-autocomplete>
                        @if (addressChanged) {
                          <mat-hint class="field-changed-hint">Unsaved changes</mat-hint>
                        }
                      </mat-form-field>
                      <div class="address-row">
                        <mat-form-field appearance="fill" class="half-width-field">
                          <mat-label>City</mat-label>
                          <input
                            matInput
                            data-testid="project-address-city"
                            [(ngModel)]="editedAddress.city"
                            (ngModelChange)="onFieldChange()"
                            placeholder="San Francisco"
                          />
                        </mat-form-field>
                        <mat-form-field appearance="fill" class="quarter-width-field">
                          <mat-label>State</mat-label>
                          <input
                            matInput
                            data-testid="project-address-state"
                            [(ngModel)]="editedAddress.state"
                            (ngModelChange)="onFieldChange()"
                            placeholder="CA"
                          />
                        </mat-form-field>
                        <mat-form-field appearance="fill" class="quarter-width-field">
                          <mat-label>Zip</mat-label>
                          <input
                            matInput
                            data-testid="project-address-postal-code"
                            [(ngModel)]="editedAddress.postalCode"
                            (ngModelChange)="onFieldChange()"
                            #postalCodeModel="ngModel"
                            pattern="[0-9]{5}(-[0-9]{4})?"
                            placeholder="94102"
                          />
                          @if (postalCodeModel.hasError('pattern')) {
                            <mat-error
                              id="project-address-postal-code-error"
                              role="alert"
                              aria-live="polite"
                              aria-atomic="true"
                            >
                              Enter a valid ZIP code, such as 94102 or 94102-1234.
                            </mat-error>
                          }
                        </mat-form-field>
                      </div>
                    </div>
                  </div>
                  <!-- System Metadata Row (read-only) -->
                  <div class="metadata-item system-metadata-row full-width-item">
                    <div class="system-field">
                      <span class="field-label">Created By</span>
                      <div class="metadata-value">{{ projectMetadata.created_by }}</div>
                    </div>
                    <div class="system-field">
                      <span class="field-label">Created At</span>
                      <div class="metadata-value">{{ formatDate(projectMetadata.created_at) }}</div>
                    </div>
                    <div class="system-field">
                      <span class="field-label">Status</span>
                      <div class="metadata-value">{{ projectMetadata.status | titlecase }}</div>
                    </div>
                  </div>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- Right Column: Project Location Map (Issue #236) -->
          @if (!loadingMetadata) {
            <app-project-location-map
              [center]="mapCenter"
              [zoom]="mapZoom"
              [address]="formatAddress(getProjectAddress())"
            ></app-project-location-map>
          }
        </div>
      </div>
    </mat-tab>

    <!-- Files Tab -->
    <mat-tab label="Files">
      <div class="tab-content">
        <!-- Project Files Section -->
        <mat-card class="settings-section">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>folder</mat-icon>
              Project Files
            </mat-card-title>
            <mat-card-subtitle>Manage files uploaded to this project</mat-card-subtitle>
          </mat-card-header>
          <button
            mat-raised-button
            color="primary"
            (click)="openUploadDialog()"
            [disabled]="uploading"
            class="mobile-upload-button"
          >
            <mat-icon>add</mat-icon>
            Upload File
          </button>
          <mat-card-content>
            <!-- Loading indicator -->
            @if (loadingFiles || loadingFileMetadata) {
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            }

            <!-- Files Table -->
            @if (!loadingFiles && !loadingFileMetadata) {
              <div class="files-table-container">
                @if (fileMetadataList.length > 0) {
                  <table mat-table [dataSource]="fileMetadataList" class="files-table">
                    <!-- File Name Column -->
                    <ng-container matColumnDef="name">
                      <th mat-header-cell *matHeaderCellDef class="name-column-header">
                        <div class="header-toolbar">
                          <span class="header-title">File name</span>
                          <button
                            mat-raised-button
                            color="primary"
                            size="small"
                            (click)="openUploadDialog()"
                            [disabled]="uploading"
                            class="header-upload-button"
                          >
                            <mat-icon>add</mat-icon>
                            Upload
                          </button>
                        </div>
                      </th>
                      <td mat-cell *matCellDef="let file" class="name-column">
                        <div class="file-name-cell">
                          <mat-icon class="file-type-icon">picture_as_pdf</mat-icon>
                          <div class="file-info">
                            <div class="file-name" [title]="file.getFileName()">{{ file.getFileName() }}</div>
                            <div class="file-subtitle">
                              <span class="document-type">{{ formatDocumentType(file.getDocumentType()) }}</span>
                              <span class="separator">|</span>
                              <span [class]="getStatusColorClass(file.getProcessingStatus())">
                                {{ formatProcessingStatus(file.getProcessingStatus()) }}
                              </span>
                              <span class="separator">|</span>
                              <span class="file-id">ID: {{ file.getFileId() }}</span>
                              <span class="separator">|</span>
                              <span class="upload-date">
                                Uploaded: {{ file.getUploadDate() | date: 'MMM d, y, h:mm:ss a' }}
                              </span>
                            </div>
                            @if (file.getMetadataError()) {
                              <div class="metadata-error" role="status">{{ file.getMetadataError() }}</div>
                            }
                          </div>
                        </div>
                      </td>
                    </ng-container>
                    <!-- File Size Column -->
                    <ng-container matColumnDef="size">
                      <th mat-header-cell *matHeaderCellDef>Size</th>
                      <td mat-cell *matCellDef="let file">
                        @if (file.getFileSizeBytes() > 0) {
                          <span>
                            {{ formatFileSize(file.getFileSizeBytes()) }}
                          </span>
                        } @else {
                          <span class="unknown-value">—</span>
                        }
                      </td>
                    </ng-container>
                    <!-- Page Count Column -->
                    <ng-container matColumnDef="pageCount">
                      <th mat-header-cell *matHeaderCellDef>Pages</th>
                      <td mat-cell *matCellDef="let file">
                        <div class="page-info">
                          @if (file.getPageCount() > 0) {
                            <span class="ingestion-status">
                              @switch (getIngestedPageCountState(file)) {
                                @case ('loading') {
                                  <span class="ingested-count-loading" role="status" aria-live="polite">
                                    Loading…
                                  </span>
                                }
                                @case ('known') {
                                  <span class="ingested-count">{{ getIngestedPageCount(file) }}</span>
                                }
                                @default {
                                  <span class="unknown-value" aria-label="Unavailable">—</span>
                                }
                              }
                              <span class="ingested-label">ingested of</span>
                              <span class="total-count">{{ file.getPageCount() }}</span>
                            </span>
                          } @else {
                            <span class="unknown-value">—</span>
                          }
                        </div>
                      </td>
                    </ng-container>
                    <!-- Actions Column -->
                    <ng-container matColumnDef="actions">
                      <th mat-header-cell *matHeaderCellDef class="actions-column-header"></th>
                      <td mat-cell *matCellDef="let file" class="actions-column">
                        <button
                          mat-icon-button
                          [attr.aria-label]="'File actions for ' + file.getFileName()"
                          [matMenuTriggerFor]="fileMenu"
                          (click)="$event.stopPropagation()"
                          matTooltip="More actions"
                        >
                          <mat-icon>more_vert</mat-icon>
                        </button>
                        <mat-menu #fileMenu="matMenu">
                          <button mat-menu-item (click)="downloadFile(file)">
                            <mat-icon>download</mat-icon>
                            <span>Download</span>
                          </button>
                          <button mat-menu-item (click)="openFilePropertiesDialog(file)">
                            <mat-icon>info</mat-icon>
                            <span>Properties</span>
                          </button>
                          @if (file.getProcessingStatus() === ProcessingStatus.PROCESSING_STATUS_FAILED) {
                            <button mat-menu-item (click)="retryFileMetadata(file)">
                              <mat-icon>refresh</mat-icon>
                              <span>Retry metadata</span>
                            </button>
                          }
                          <mat-divider></mat-divider>
                          <button mat-menu-item class="delete-action" (click)="deleteFile(file)">
                            <mat-icon>delete</mat-icon>
                            <span>Delete</span>
                          </button>
                        </mat-menu>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr
                      mat-row
                      *matRowDef="let row; columns: displayedColumns"
                      class="clickable-row"
                      data-testid="project-file-row"
                      [attr.data-file-id]="row.getFileId()"
                      [attr.data-file-name]="row.getFileName()"
                      [attr.data-file-size-bytes]="row.getFileSizeBytes()"
                      [attr.data-page-count]="row.getPageCount()"
                      [attr.data-ingested-page-count-state]="getIngestedPageCountState(row)"
                      [attr.data-ingested-page-count]="getIngestedPageCountOrNull(row)"
                      [attr.data-document-type]="formatDocumentType(row.getDocumentType())"
                      [attr.data-processing-status]="formatProcessingStatus(row.getProcessingStatus())"
                      (click)="onFileRowClick(row)"
                      [matTooltip]="
                        row.getProcessingStatus() === ProcessingStatus.PROCESSING_STATUS_FAILED
                          ? 'Metadata generation failed; use File actions to retry or delete'
                          : 'Click to process pages from this file'
                      "
                    ></tr>
                  </table>
                } @else {
                  <div class="no-files-state">
                    <mat-icon class="no-files-icon">folder_open</mat-icon>
                    <h3>No files uploaded yet</h3>
                    <p>Upload your first PDF file to get started with this project</p>
                    <button mat-raised-button color="primary" (click)="openUploadDialog()">
                      <mat-icon>add</mat-icon>
                      Upload File
                    </button>
                  </div>
                }
                <!-- Table Footer with Directory Info -->
                @if (fileMetadataList.length > 0) {
                  <div class="table-footer">
                    <mat-icon class="footer-icon">folder</mat-icon>
                    <span class="footer-path">{{ projectId }}/inputs</span>
                    <span class="footer-spacer"></span>
                    <span class="footer-count">
                      {{ fileMetadataList.length }} {{ fileMetadataList.length === 1 ? 'file' : 'files' }}
                    </span>
                  </div>
                }
                <!-- No Files State -->
              </div>
            }
          </mat-card-content>
        </mat-card>

        <!-- Cross-Reference Graph (Issue #1066) -->
        <mat-card class="settings-section">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>hub</mat-icon>
              Cross-Reference Graph
            </mat-card-title>
            <mat-card-subtitle> Discover cross-page relationships between architectural plan pages </mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <p class="cross-ref-description">
              Analyzes all ingested pages to find detail callouts, schedule references, general notes, and other
              cross-page dependencies. This builds a graph that enriches page transcripts with cross-page context.
            </p>
            <div class="cross-ref-actions">
              <button
                mat-raised-button
                color="primary"
                (click)="buildCrossReferenceGraph()"
                [disabled]="buildingCrossRefGraph"
              >
                @if (buildingCrossRefGraph) {
                  <mat-icon>hourglass_empty</mat-icon>
                  Building...
                } @else {
                  <mat-icon>hub</mat-icon>
                  Build Cross-References
                }
              </button>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <!-- Tasks Tab (Issue #1112) -->
    <mat-tab>
      <ng-template mat-tab-label>
        Tasks
        @if (activeTaskCount > 0) {
          <span class="task-count-badge">{{ activeTaskCount }}</span>
        }
      </ng-template>
      <app-tasks-page></app-tasks-page>
    </mat-tab>

    <!-- Settings Tab (Issue #1702) -->
    <mat-tab label="Settings">
      <div class="tab-content">
        @if (loadingMetadata) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }

        @if (!loadingMetadata) {
          <mat-card class="settings-section toc-display-settings-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>toc</mat-icon>
                Table of contents
              </mat-card-title>
              <mat-card-subtitle>Choose which review signals appear beside drawing sheets</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="toc-warning-setting-row">
                <div class="toc-warning-setting-copy">
                  <span class="toc-warning-setting-name">Show compliance warnings</span>
                  <span class="toc-warning-setting-description">
                    Shows non-zero High and Medium compliance-result counts beside each sheet. Processing states such as
                    Pending, Missing, Stale, or Failed remain visible.
                  </span>
                </div>
                <mat-slide-toggle
                  aria-label="Show compliance warnings in the table of contents"
                  [checked]="showTableOfContentsWarnings"
                  (change)="onTableOfContentsWarningsChanged($event.checked)"
                ></mat-slide-toggle>
              </div>
            </mat-card-content>
          </mat-card>
        }

        <!-- Model Configuration Card (Issue #1020) -->
        @if (hasProjectMetadata && !loadingMetadata) {
          <app-model-config-card
            [projectId]="projectId"
            [projectModelConfig]="projectMetadata?.modelConfig || null"
          ></app-model-config-card>
        }

        <!-- Ingestion Settings Card (Issue #1151) -->
        @if (hasProjectMetadata && !loadingMetadata) {
          <app-ingestion-settings-card
            [projectId]="projectId"
            [ingestionSettings]="projectMetadata?.ingestionSettings || null"
          ></app-ingestion-settings-card>
        }

        <!-- Power User Mode Card (Issue #1228) — only visible to root admins -->
        @if (hasProjectMetadata && !loadingMetadata && isRootAdmin) {
          <app-power-user-mode-card
            [projectId]="projectId"
            [enabled]="projectMetadata?.power_user_mode ?? false"
            (enabledChange)="onPowerUserModeChanged($event)"
          ></app-power-user-mode-card>
        }

        @if (!hasProjectMetadata && !loadingMetadata) {
          <div class="no-files-state">
            <mat-icon class="no-files-icon">info</mat-icon>
            <h3>Project settings unavailable</h3>
            <p>Please set up project metadata in the Overview tab first to configure these settings.</p>
          </div>
        }
      </div>
    </mat-tab>

    <!-- CAT graphs Tab (Issue #2399). Loaded only when opened, so other tabs do not fetch graphs. -->
    <mat-tab label="CAT graphs">
      <ng-template matTabContent>
        <app-cat-graphs-page></app-cat-graphs-page>
      </ng-template>
    </mat-tab>
  </mat-tab-group>
</div>
